import { TextButton } from '@creditornot/cb-button';
import { Pencil as EditIcon } from '@creditornot/cb-icons';
import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import React, { useState, useEffect } from 'react';
import { Form, Field } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { VenuePatchData } from 'apis/restaurant-api/merchant-admin/venue/types';
import { Typography as T } from 'common/typography';

type Props = {
  editable: boolean;
  field: {
    label: string;
    name: 'phone' | 'website';
    value: string;
    validation: (value: string | undefined) => string | null;
  };
  onSubmit: (data: VenuePatchData) => void;
};

const EditButtons = styled.div``;

const CancelButton = styled(TextButton).attrs({ size: 'small', variant: 'grey' })`
  margin-inline-end: 12px;
`;

const SaveButton = styled(TextButton).attrs({ size: 'small' })``;

const EditButton = styled(TextButton).attrs({ size: 'small' })``;

const FormItemRoot = styled.form`
  display: flex;
  flex-flow: column;
  padding: 25px 0;
  width: 100%;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  margin-block-end: 10px;
`;

const Label = styled(T.Small)`
  display: flex;
  text-transform: capitalize;
  color: ${cssThemedValue('textSecondary')};
`;

const ViewContent = styled(T.Body3)`
  color: ${cssThemedValue('textPrimary')};
  letter-spacing: -0.154px;
  overflow-wrap: break-word;
`;

const ErrorMessage = styled(T.Small)`
  color: ${colors.strawberry100};
  padding: 5px 0;
`;

const StyledInput = styled(Input)`
  width: 100%;
`;

const EditableFormItem = ({ editable, field, onSubmit }: Props) => {
  const { label, name, value, validation } = field;

  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // TODO: rethink submission handling, this seems hacky
  useEffect(() => {
    if (submitting) {
      setEditing(false);
      setSubmitting(false);
    }
  }, [field.value]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditToggle = () => {
    setEditing(!editing);
  };

  const handleFormSubmit = (values: VenuePatchData) => {
    setSubmitting(true);
    onSubmit(values);
  };

  return (
    <Form initialValues={{ [name]: value }} onSubmit={handleFormSubmit}>
      {({ form, handleSubmit, pristine, valid }) => {
        const handleCancel = () => {
          setEditing(false);
          form.reset();
        };

        return (
          <FormItemRoot onSubmit={handleSubmit}>
            <Top>
              <Label>
                <FormattedMessage id={label} />
              </Label>
              {editable && (
                <EditButtons>
                  {editing ? (
                    <>
                      <CancelButton
                        onClick={handleCancel}
                        disabled={submitting}
                        // The type prop is important, because otherwise form does not submit when pressing enter.
                        type="button"
                      >
                        <FormattedMessage id="state.cancel" />
                      </CancelButton>
                      <SaveButton disabled={pristine || submitting || !valid} onClick={handleSubmit}>
                        <FormattedMessage id="state.save" />
                      </SaveButton>
                    </>
                  ) : (
                    <EditButton icon={<EditIcon />} onClick={handleEditToggle}>
                      <FormattedMessage id="state.edit" />
                    </EditButton>
                  )}
                </EditButtons>
              )}
            </Top>
            {editing ? (
              <Field<string> name={name} validate={validation}>
                {({ input, meta }) => (
                  <>
                    <StyledInput {...input} autoComplete="off" />
                    {!!meta.error && (
                      <ErrorMessage>
                        <FormattedMessage id={meta.error} />
                      </ErrorMessage>
                    )}
                  </>
                )}
              </Field>
            ) : (
              <ViewContent>{value}</ViewContent>
            )}
          </FormItemRoot>
        );
      }}
    </Form>
  );
};

export default EditableFormItem;
